import { createSelector } from '@reduxjs/toolkit';
import ms from 'ms';

import type { GlobalState } from '@/redux/store';

import { getRecentSearchQuery } from '@/redux/modules/search/save/saveSearch.helpers';
import { sanitizeSearchKey } from '@/utils/strings';

const HEADER_RECENT_SEARCHES_DISPLAY_COUNT = 5;
export const RECENT_SEARCHES_CACHE_TIME = ms('5m');

const getByParams = (state: GlobalState) => state.saveSearch.byParams;

const getRecentSearches = (state: GlobalState) => state.saveSearch.recentSearches;

export const getNewSignupSearchParams = (state: GlobalState) => state.saveSearch.newSignupSearchParams;

export const getSaveSearchSubmitted = (state: GlobalState) => state.saveSearch.submitted;

export const getSaveSearchSuccess = (state: GlobalState) => state.saveSearch.success;

export const getHasSavedSearches = (state: GlobalState) =>
    Object.values(getByParams(state)).some((savedSearch) => !savedSearch.deleted);

export const getIsSavedSearch = (state: GlobalState, saveSearchKey: number | string) => {
    const savedSearch = getByParams(state)[sanitizeSearchKey(String(saveSearchKey))];
    return Boolean(savedSearch && !savedSearch.deleted);
};

export const getSavedSearchId = (state: GlobalState, saveSearchKey: number | string) =>
    getByParams(state)[sanitizeSearchKey(String(saveSearchKey))]?.savedSearchId || 0;

export const getSavedSearchKeyById = (state: GlobalState, id: number | string) => {
    let savedSearchKey = '';
    Object.values(getByParams(state)).forEach((savedSearch) => {
        if (savedSearch.savedSearchId === Number(id)) {
            savedSearchKey = savedSearch.key;
        }
    });

    return savedSearchKey;
};

export type HeaderRecentSearch = {
    hash: string;
    query: string;
    url: string;
};

export const getHeaderRecentSearches = createSelector(getRecentSearches, (recentSearches): HeaderRecentSearch[] =>
    recentSearches.slice(0, HEADER_RECENT_SEARCHES_DISPLAY_COUNT).flatMap((recentSearch) => {
        const query = getRecentSearchQuery(recentSearch);

        if (!query) {
            return [];
        }

        return [{ hash: recentSearch.hash, query, url: recentSearch.url }];
    })
);

export const shouldFetchRecentSearches = (state: GlobalState, force: boolean) => {
    if (force) {
        return true;
    }

    if (state.saveSearch.recentSearchesLoading) {
        return false;
    }

    const recentSearchesLoaded = state.saveSearch.recentSearchesLoaded;

    if (!recentSearchesLoaded) {
        return true;
    }

    return Date.now() - recentSearchesLoaded >= RECENT_SEARCHES_CACHE_TIME;
};
